import { useState, useCallback } from 'react';
import axios from 'axios';
import { enhancedApi } from '@/shared/services/enhancedApi';

// Policy verification data interface
interface PolicyVerificationData {
  planName: string;
  occNumber: string | null;
  policyHolder: string;
  validityPeriod: string;
  coverage_status: 'active' | 'inactive';
}

// API response interface
interface PolicyVerificationResponse {
  success: boolean;
  code: number;
  data?: PolicyVerificationData;
  message: string;
}

// Hook return interface
interface UseQRCodeVerificationReturn {
  verifyQRCode: (verificationId: string) => Promise<PolicyVerificationResponse>;
  loading: boolean;
  error: string | null;
  data: PolicyVerificationData | null;
  clearState: () => void;
}

export const useQRCodeVerification = (): UseQRCodeVerificationReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PolicyVerificationData | null>(null);

  // Main verification function
  const verifyQRCode = useCallback(async (
    verificationId: string
  ): Promise<PolicyVerificationResponse> => {
    setLoading(true);
    setError(null);
    setData(null);

    try {
      // Validate required input
      if (!verificationId || !verificationId.trim()) {
        throw new Error('Verification ID is required');
      }

      const trimmedId = verificationId.trim();
      console.log('QR Code verification request:', { verificationId: trimmedId });

      // Make API call using enhancedApi with caching
      const response: PolicyVerificationResponse = await enhancedApi.cachedGet(
        `/api/transactions/validate/${trimmedId}`,
        {
          timeout: 15000, // 15 second timeout
          headers: {
            'Content-Type': 'application/json',
          }
        },
        2 * 60 * 1000, // 2 minutes cache TTL for verification data
        1 // High priority
      );

      console.log('QR Code verification response:', response);

      if (response.success && response.data) {
        setData(response.data);
        console.log('QR Code verification successful:', response.data);
        return response;
      } else {
        // Handle API success=false or missing data
        const errorMessage = response.message || 'Verification failed';
        throw new Error(errorMessage);
      }

    } catch (err) {
      console.error('Error verifying QR code:', err);
      
      let errorMessage = 'Failed to verify QR code';
      
      if (axios.isAxiosError(err)) {
        if (err.response) {
          // Server responded with error status
          const serverMessage = err.response.data?.message || err.response.statusText;
          const statusCode = err.response.status;
          
          // Handle specific error codes
          if (statusCode === 404) {
            errorMessage = 'Invalid verification ID or policy not found';
          } else if (statusCode === 400) {
            errorMessage = 'Invalid request format';
          } else if (statusCode >= 500) {
            errorMessage = 'Server error. Please try again later.';
          } else {
            errorMessage = `Server error (${statusCode}): ${serverMessage}`;
          }
        } else if (err.request) {
          // Request was made but no response received
          errorMessage = 'Network error: Unable to connect to server. Please check your internet connection.';
        } else {
          // Something else happened
          errorMessage = `Request error: ${err.message}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Return error response
      return {
        success: false,
        code: axios.isAxiosError(err) && err.response?.status ? err.response.status : 500,
        message: errorMessage
      };

    } finally {
      setLoading(false);
    }
  }, []);

  // Clear all state
  const clearState = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  return {
    verifyQRCode,
    loading,
    error,
    data,
    clearState
  };
};